import { z } from 'zod'
import { baseEntitySchema } from '../../core/entity.js'
import { gearRequirementSchema } from '../gear/requirement.js'

/**
 * A band member's requirements within a rider
 */
export const riderMemberSchema = z.object({
  /** User ID of the band member */
  userId: z.string(),
  /** Role/position (e.g., "Vocals", "Guitar") - can override profile */
  role: z.string().optional(),
  /** Gear requirements for this member */
  requirements: z.array(gearRequirementSchema).default([]),
  /** Channel number on the input list */
  channelNumber: z.number().optional(),
  /** Monitor mix number */
  monitorMix: z.number().optional(),
})

export type RiderMember = z.infer<typeof riderMemberSchema>

/**
 * Monitor requirements for the band
 */
export const riderMonitorsSchema = z.object({
  /** Number of wedge monitors */
  wedges: z.number().optional(),
  /** Number of IEM packs */
  iemPacks: z.number().optional(),
  /** Number of separate monitor mixes */
  mixes: z.number().optional(),
})

export type RiderMonitors = z.infer<typeof riderMonitorsSchema>

/**
 * Stage size requirements
 */
export const stageSizeSchema = z.object({
  /** Width in meters */
  width: z.number(),
  /** Depth in meters */
  depth: z.number(),
})

export type StageSize = z.infer<typeof stageSizeSchema>

/**
 * Shared/band-level technical requirements
 */
export const riderSharedSchema = z.object({
  /** Backline requirements (amps, drums, keyboards) */
  backline: z.array(gearRequirementSchema).optional(),
  /** Monitor requirements */
  monitors: riderMonitorsSchema.optional(),
  /** Minimum stage size */
  stageSize: stageSizeSchema.optional(),
  /** Power requirements */
  power: z.string().optional(),
  /** PA system notes */
  paSystem: z.string().optional(),
})

export type RiderShared = z.infer<typeof riderSharedSchema>

/**
 * Hospitality requirements
 */
export const riderHospitalitySchema = z.object({
  /** Number of meals required */
  meals: z.number().optional(),
  /** Drink requests */
  drinks: z.array(z.string()).optional(),
  /** Number of parking spaces */
  parkingSpaces: z.number().optional(),
  /** Additional hospitality notes */
  notes: z.string().optional(),
})

export type RiderHospitality = z.infer<typeof riderHospitalitySchema>

/**
 * Technical rider entity - specifies requirements for a band's performance
 */
export const riderSchema = baseEntitySchema.extend({
  type: z.literal('rider'),

  // Event details
  /** Name of the event/gig */
  eventName: z.string().optional(),
  /** Venue name */
  venue: z.string().optional(),
  /** Event date */
  eventDate: z.string().optional(),

  // Per-member requirements
  /** Requirements for each band member */
  members: z.array(riderMemberSchema).default([]),

  // Shared requirements
  /** Band-level technical requirements */
  shared: riderSharedSchema.optional(),

  // Hospitality
  /** Hospitality requirements */
  hospitality: riderHospitalitySchema.optional(),

  // Generation metadata
  /** Whether this rider was auto-generated from member profiles */
  generatedFromProfiles: z.boolean().optional(),
})

export type Rider = z.infer<typeof riderSchema>

export function isRider(node?: unknown): node is Rider {
  return riderSchema.safeParse(node).success
}
